import { z } from 'zod';
import { SportNameSchema } from './profile.js';
// F20 — type-only imports to populate the `recentPaymentMarks` + `openDispute`
// fields on BookingDetail. `type` keyword erases at runtime so the
// payment.ts ↔ booking.ts cycle (payment.ts imports PaymentStatus from here)
// is type-level only and safe under TypeScript's structural inference.
import type { PaymentMark, PaymentDispute } from './payment.js';

/**
 * F16 — Reservation Engine frozen API contracts (booking surface).
 *
 * See plans/16_F16_reservation_engine.md (Task 1.2) for the founder-frozen
 * shape of every Zod schema and type alias below. Sub-agents in Wave 2
 * develop against these contracts in parallel — DO NOT rename, drop, or add
 * fields without updating the plan first.
 *
 * F18 extends the enum + adds three walk-in input schemas (`CreateWalkInInput`,
 * `EditWalkInInput`, `CancelWalkInInput`) + three new `BookingErrorCode`
 * literals. Walk-ins ride the SAME `bookings` row discriminated by
 * `kind='walk_in'`; the venue-staff-driven path lands at
 * `status='confirmed' + payment_status='paid'` and `host_user_id IS NULL`.
 */

// ─── Enum constants ──────────────────────────────────────────────────────────

/**
 * F16 shipped `['booking', 'blockout']`; F18 widens to admit `'walk_in'`.
 * Walk-ins reuse the F16 row layout + EXCLUDE concurrency guard; the
 * discriminator alone gates F23 commission exclusion + F38 quota counters +
 * F44/F45 reporting.
 */
export const BOOKING_KIND_VALUES = ['booking', 'blockout', 'walk_in'] as const;
export type BookingKind = (typeof BOOKING_KIND_VALUES)[number];

/**
 * Lifecycle status enum. F17 dropped `'in_progress'` from F16's original
 * 6-value set because the auto-checked-in scenario is not implemented at
 * MVP (founder Decision 1 — "if booking not canceled then for MVP let us
 * consider it as completed when the time pass"). The reservation flow is
 * pending → confirmed → completed, with `cancelled` and `no_show` as
 * terminal off-ramps. `no_show` is a manual venue action only.
 */
export const BOOKING_STATUS_VALUES = [
  'pending',
  'confirmed',
  'completed',
  'cancelled',
  'no_show',
] as const;
export type BookingStatus = (typeof BOOKING_STATUS_VALUES)[number];

/**
 * Orthogonal payment-state column added in F17 (Decision 1). F17 ships the
 * column + state machine + the `markPaymentStatus` service hook; F20 writes
 * the venue-staff "Mark paid" route and UI. F23 commission engine triggers
 * on `paid`.
 *
 * State machine (allowed transitions):
 *   unpaid               → pending_confirmation | paid | refunded
 *   pending_confirmation → paid | refunded | unpaid
 *   paid                 → refunded
 *   refunded             → ∅   (terminal — partial refunds modelable post-MVP)
 */
export const PAYMENT_STATUS_VALUES = [
  'unpaid',
  'pending_confirmation',
  'paid',
  'refunded',
] as const;
export type PaymentStatus = (typeof PAYMENT_STATUS_VALUES)[number];

export const BOOKING_SOURCE_VALUES = ['mobile', 'portal', 'admin'] as const;
export type BookingSource = (typeof BOOKING_SOURCE_VALUES)[number];

/**
 * Discriminator for `force-transition` admin endpoint — selects which
 * orthogonal column the override mutates.
 */
export const FORCE_TRANSITION_TARGETS = ['lifecycle', 'payment'] as const;
export type ForceTransitionTarget = (typeof FORCE_TRANSITION_TARGETS)[number];

export const BOOKING_ERROR_CODES = [
  'SLOT_TAKEN',
  'OUTSIDE_OPENING_HOURS',
  'CLOSED_ON_DATE',
  'OUTSIDE_BOOKING_WINDOW',
  'BELOW_MIN_LEAD',
  'BEYOND_MAX_ADVANCE',
  'NO_PRICE_BAND',
  'SPORT_NOT_HOSTED',
  'PARTY_SIZE_EXCEEDS_CAPACITY',
  'VENUE_NOT_BOOKABLE',
  'INVALID_DURATION',
  'IDEMPOTENT_REPLAY_PAYLOAD_MISMATCH',
  'INVALID_TRANSITION',
  'NOT_OWN_BOOKING',
  // F17 — booking lifecycle additions
  'INVALID_PAYMENT_TRANSITION',
  'CANCELLATION_OUTSIDE_WINDOW',
  'RESCHEDULE_LIMIT_REACHED',
  'RESCHEDULE_OUTSIDE_WINDOW',
  'POLICY_INVALID_SHAPE',
  'FORCE_TRANSITION_REQUIRES_REASON',
  // F18 — walk-in tracking additions.
  // `WALK_IN_VENUE_NOT_BOOKABLE` — venue.bookable=false at create time (Decision 1).
  // `WALK_IN_HOST_USER_FORBIDDEN` — caller passed a hostUserId on a walk-in
  //   create/edit; walk-ins are anonymous-only at MVP (Decision 11).
  // `WALK_IN_KIND_MISMATCH` — caller attempted edit/cancel on a non-walk-in
  //   row via a walk-in route (or vice versa).
  'WALK_IN_VENUE_NOT_BOOKABLE',
  'WALK_IN_HOST_USER_FORBIDDEN',
  'WALK_IN_KIND_MISMATCH',
  // FIX journey audit 1.8 (B3) — a reschedule stays at the booking's venue,
  // and a walk-in edit can only pick a court of its own venue.
  'RESCHEDULE_VENUE_MISMATCH',
  'SUB_VENUE_NOT_IN_VENUE',
] as const;
export type BookingErrorCode = (typeof BOOKING_ERROR_CODES)[number];

// ─── Zod input schemas ───────────────────────────────────────────────────────

export const CreateBookingInputSchema = z.object({
  subVenueId: z.string().uuid(),
  sportName: z.string().min(1).max(40),
  startAt: z.string().datetime({ offset: true }),
  endAt: z.string().datetime({ offset: true }),
  partySize: z.number().int().min(1).max(50).default(1),
  notes: z.string().max(500).optional(),
  // F40 — explicit promotion code the booker entered (optional). Redemption
  // happens via this field on POST /me/bookings (no separate redeem route, per
  // plan §9). When present, createBooking reserves the discount inside the
  // booking tx and FAILS loudly if the code does not apply. Absent → the
  // auto-match path runs (silent — no error when no auto-promo applies).
  appliedPromotionCode: z.string().min(3).max(32).optional(),
});
export type CreateBookingInput = z.infer<typeof CreateBookingInputSchema>;

export const AcceptBookingInputSchema = z.object({}).strict();
export type AcceptBookingInput = z.infer<typeof AcceptBookingInputSchema>;

export const DeclineBookingInputSchema = z.object({
  reason: z.string().max(500).optional(),
});
export type DeclineBookingInput = z.infer<typeof DeclineBookingInputSchema>;

export const CancelBookingInputSchema = z.object({
  reason: z.string().max(500).optional(),
});
export type CancelBookingInput = z.infer<typeof CancelBookingInputSchema>;

export const CreateBlockoutInputSchema = z.object({
  subVenueId: z.string().uuid(),
  startAt: z.string().datetime({ offset: true }),
  endAt: z.string().datetime({ offset: true }),
  reason: z.string().min(1).max(500),
});
export type CreateBlockoutInput = z.infer<typeof CreateBlockoutInputSchema>;

export const BookingListFilterSchema = z.object({
  status: z
    .union([z.enum(BOOKING_STATUS_VALUES), z.array(z.enum(BOOKING_STATUS_VALUES))])
    .optional(),
  sportName: z.string().max(40).optional(),
  subVenueId: z.string().uuid().optional(),
  dateFrom: z.string().date().optional(),
  dateTo: z.string().date().optional(),
  cursor: z.string().optional(),
  // Parsed from the query string, so coerce ("100" → 100).
  limit: z.coerce.number().int().min(1).max(100).default(20),
});
export type BookingListFilter = z.infer<typeof BookingListFilterSchema>;

// ─── F17 — Booking lifecycle input schemas ───────────────────────────────────

/**
 * `POST /me/bookings/:id/reschedule` (booker) and
 * `POST /me/venues/:venueId/bookings/:id/reschedule` (venue staff) share the
 * same payload shape. The new slot is re-validated against F16's availability
 * rules + the EXCLUDE constraint at the DB level. Pricing is re-snapshotted
 * from the active band for the new slot.
 */
export const RescheduleBookingInputSchema = z.object({
  subVenueId: z.string().uuid(),
  sportName: z.string().min(1).max(40),
  startAt: z.string().datetime({ offset: true }),
  endAt: z.string().datetime({ offset: true }),
});
export type RescheduleBookingInput = z.infer<typeof RescheduleBookingInputSchema>;

/**
 * `POST /me/venues/:venueId/bookings/:id/complete` — venue staff manually
 * marks a confirmed booking as completed. Empty body; transition is
 * unambiguous. The auto-tick (`tickAutoComplete`) calls the same service
 * method but bypasses HTTP entirely.
 */
export const CompleteBookingInputSchema = z.object({}).strict();
export type CompleteBookingInput = z.infer<typeof CompleteBookingInputSchema>;

/**
 * `POST /me/venues/:venueId/bookings/:id/no_show` — venue staff manually
 * marks a confirmed booking as no-show. Optional note is appended to the
 * history row's `reason` for context (e.g. "Player called 15 min late").
 */
export const NoShowBookingInputSchema = z.object({
  note: z.string().max(500).optional(),
});
export type NoShowBookingInput = z.infer<typeof NoShowBookingInputSchema>;

/**
 * `POST /admin/bookings/:id/force-transition` — super_admin bypasses the
 * state-machine guard for incident response. `target` selects which
 * orthogonal column to mutate. `reason` is mandatory and audit-logged.
 *
 * The runtime `to` value is validated against the chosen target's enum at
 * the route handler (a discriminated-union Zod schema would over-constrain
 * because both enums share string types).
 */
export const ForceTransitionInputSchema = z.object({
  target: z.enum(FORCE_TRANSITION_TARGETS),
  to: z.string().min(1).max(40),
  reason: z.string().min(1).max(500),
});
export type ForceTransitionInput = z.infer<typeof ForceTransitionInputSchema>;

/**
 * `BookingService.markPaymentStatus` — F17 ships the service method + this
 * schema; F20 attaches the venue-staff route that calls it. The admin
 * force-transition path with `target: 'payment'` is the only F17 caller.
 */
export const MarkPaymentStatusInputSchema = z.object({
  to: z.enum(PAYMENT_STATUS_VALUES),
  reason: z.string().max(500).optional(),
});
export type MarkPaymentStatusInput = z.infer<typeof MarkPaymentStatusInputSchema>;

/**
 * Refund eligibility snapshot recorded on a cancelled booking. F17 writes
 * the columns; F19/F20 read them when settling actual money movement. NULL
 * on non-cancelled bookings + blockouts.
 */
export type RefundRecord = {
  readonly refundPercent: number;
  readonly refundAmount: number; // USD minor units (per F05 snapshot)
  readonly refundEligibleAt: string; // ISO UTC
};

// ─── Read-side type aliases (server → client projections) ────────────────────

export type BookingPriceQuote = {
  readonly priceAmount: number;
  readonly priceCurrency: 'USD';
  readonly taxMode: 'inclusive' | 'exclusive' | 'none';
  readonly taxRatePercent: number;
  readonly taxAmount: number;
  readonly fxRateId: string;
  readonly pricingBandId: string;
};

export type BookingSummary = {
  readonly id: string;
  readonly kind: BookingKind;
  readonly status: BookingStatus;
  readonly venueId: string;
  readonly venueName: string;
  readonly subVenueId: string;
  readonly subVenueName: string;
  readonly sportName: string;
  readonly startAt: string; // ISO UTC
  readonly endAt: string; // ISO UTC
  readonly venueTimezone: string;
  readonly partySize: number;
  readonly price: BookingPriceQuote;
  /** The booker's display name. Set on trainer lists (`GET /me/trainer/bookings`) and details. */
  readonly hostDisplayName?: string | null;
  /** Set on trainer lists (`GET /me/trainer/bookings`) and details. */
  readonly paymentStatus?: PaymentStatus;
  /** F33 — present on trainer sessions so lists can say who the session is with. */
  readonly trainerDisplayName?: string | null;
  /** Trainer sessions follow their own lifecycle and cannot use court rescheduling. */
  readonly trainerId?: string | null;
};

// ─── F18 — Walk-in input schemas ─────────────────────────────────────────────

/**
 * `POST /me/venues/:venueId/walk-ins` — venue-staff logs a non-app booking.
 *
 * - `walkInName`/`walkInPhone` are optional (Decision 3 — anonymous walk-ins
 *   allowed; the customer is physically present at the desk).
 * - `walkInPhone` validates as E.164 (`+CC...`). Length 7–15 digits after `+`.
 * - `idempotencyKey` is OPTIONAL; when present the API short-circuits replays
 *   within 24 h via the venue-scoped partial unique index
 *   `bookings_walkin_idempotency_idx` (Decision 12 — walk-ins have NULL
 *   `host_user_id`, so the F16 host-scoped index does not apply).
 */
export const CreateWalkInInputSchema = z.object({
  subVenueId: z.string().uuid(),
  sportName: SportNameSchema,
  startAt: z.string().datetime({ offset: true }),
  endAt: z.string().datetime({ offset: true }),
  partySize: z.number().int().min(1).max(32),
  walkInName: z.string().min(1).max(80).optional(),
  walkInPhone: z
    .string()
    .regex(/^\+[1-9]\d{6,14}$/u, 'Must be E.164')
    .optional(),
  notes: z.string().max(500).optional(),
  idempotencyKey: z.string().uuid().optional(),
});
export type CreateWalkInInput = z.infer<typeof CreateWalkInInputSchema>;

/**
 * `PATCH /me/venues/:venueId/walk-ins/:id` — Owner / Manager only
 * (Decision 5 — front-desk creates but cannot edit / cancel). At least one
 * field must be provided. The F16 EXCLUDE constraint re-runs against any
 * (sub_venue, time-range) change. No reschedule-count cap, no lead-time
 * guard — walk-ins are retroactive end-of-day entry by design.
 *
 * Nullable strings (`walkInName`, `walkInPhone`, `notes`) allow explicit
 * "clear this field" intent vs. omitting (no-op).
 */
export const EditWalkInInputSchema = z
  .object({
    subVenueId: z.string().uuid().optional(),
    sportName: SportNameSchema.optional(),
    startAt: z.string().datetime({ offset: true }).optional(),
    endAt: z.string().datetime({ offset: true }).optional(),
    partySize: z.number().int().min(1).max(32).optional(),
    walkInName: z.string().max(80).nullable().optional(),
    walkInPhone: z
      .string()
      .regex(/^\+[1-9]\d{6,14}$/u)
      .nullable()
      .optional(),
    notes: z.string().max(500).nullable().optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'At least one field required' });
export type EditWalkInInput = z.infer<typeof EditWalkInInputSchema>;

/**
 * `POST /me/venues/:venueId/walk-ins/:id/cancel` — Owner / Manager only.
 * Records refund eligibility via F17's `CancellationPolicyService`
 * symmetrically with app bookings (Decision 4). The `booking.refund_recorded`
 * notification event is wired but never fires for walk-ins at MVP because
 * `host_user_id IS NULL` (Decision 9 — no user-facing notifications).
 */
export const CancelWalkInInputSchema = z.object({
  reason: z.string().max(280).optional(),
});
export type CancelWalkInInput = z.infer<typeof CancelWalkInInputSchema>;

// ─── Read-side type aliases (continued) ──────────────────────────────────────

export type BookingDetail = BookingSummary & {
  readonly hostUserId: string | null;
  readonly hostDisplayName: string | null;
  readonly affiliationId: string | null;
  readonly teamId: string | null;
  readonly source: BookingSource;
  readonly acceptedAt: string | null;
  readonly cancelledAt: string | null;
  readonly cancelledByUserId: string | null;
  readonly cancelledReason: string | null;
  readonly blockoutReason: string | null;
  readonly notes: string | null;
  // F17 — orthogonal payment-state column (Decision 1). F17 ships read-only;
  // F20 wires the venue-staff "Mark paid" mutator. F23 commission engine
  // triggers when this flips to 'paid' (Decision 7).
  readonly paymentStatus: PaymentStatus;
  // F17 — refund eligibility snapshot (Decision 3). Non-null only on cancelled
  // bookings. F17 records the eligibility; F19/F20 settle the actual money.
  readonly refund: RefundRecord | null;
  // F17 — set by both `tickAutoComplete` and manual `completeBooking` paths
  // (Decision 5). Null on non-completed bookings.
  readonly completedAt: string | null;
  // F17 — set only by the manual `noShowBooking` path. No auto-no-show at
  // MVP per Decision 5.
  readonly noShowMarkedAt: string | null;
  readonly noShowMarkedByUserId: string | null;
  // F17 — per-booking reschedule history (Decision 4). `rescheduleCount` is
  // capped at 5 by the DB CHECK and the service-layer guard returns
  // `RESCHEDULE_LIMIT_REACHED` at the boundary. Booking IDs stay stable
  // across reschedules (mutate-in-place semantics).
  readonly rescheduleCount: number;
  readonly lastRescheduledAt: string | null;
  readonly lastRescheduledByUserId: string | null;
  // F17 — records the LAST admin who force-transitioned the booking via the
  // `bookings:override` permission. Full history is in `history` below with
  // `actorKind === 'admin'`. Null when no force-transition ever applied.
  readonly forceTransitionAdminId: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly history: ReadonlyArray<{
    readonly fromStatus: BookingStatus | null;
    readonly toStatus: BookingStatus;
    readonly reason: string | null;
    readonly actorKind: 'booker' | 'venue_staff' | 'admin' | 'system';
    /** Lets a trainer session label the trainer's own transitions (they log as venue_staff). */
    readonly actorUserId?: string | null;
    readonly surface: 'mobile' | 'portal' | 'admin' | 'api' | 'system';
    readonly createdAt: string;
  }>;
  // F20 — payment-marks domain ledger (latest 5) embedded for the booking
  // detail pane on portal B2C + B2B + admin and mobile. Full paginated history
  // is GET /me/venues/:venueId/bookings/:bookingId/payment-marks. Optional
  // because pre-F20 service code (and some test fixtures) won't populate the
  // field — frontend reads `?? []`. See plans/20_F20_off_platform_payment_marking.md.
  readonly recentPaymentMarks?: ReadonlyArray<PaymentMark>;
  // F20 — the single open `payment_disputes` row for this booking, or null
  // when no dispute is open. DB partial unique index enforces at-most-one
  // open dispute per booking (Decision 7). Optional for the same reason as
  // `recentPaymentMarks` above — pre-F20 code paths default to undefined.
  readonly openDispute?: PaymentDispute | null;
  // FIX 4.9 (D9) — the last instant the booker can cancel or reschedule: start
  // minus the court's own `min_cancel_lead_minutes` (a trainer session: the
  // start, its policy only changes the refund). Clients read this instead of
  // assuming a 120-minute lead. Optional for fixtures that predate it.
  readonly cancellableUntil?: string | null;
};
